import { RefObject, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import ManualShutdownTroubleshoot from '../../../../assets/images/pluvaAutoCustomerService/manual_shutdown-troubleshoot.jpg';
import ManualStartTroubleshoot from '../../../../assets/images/pluvaAutoCustomerService/manual_start-troubleshoot.jpg';
import ManualStraightLineAssist from '../../../../assets/images/pluvaAutoCustomerService/manual_straight-line-assist.jpg';
import ManualWirelessUpdate from '../../../../assets/images/pluvaAutoCustomerService/manual_wireless-update.jpg';
import TractorGuideAttachmentMachine from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_attachment-machine.jpg';
import TractorGuideDualTractorUsage from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_dual-tractor-usage.jpg';
import TractorGuideFeatureSet from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_feature-set.jpg';
import TractorGuideGPSConnected from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_GPS-connected.jpg';
import TractorGuidePrepareUturn from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_prepare-uturn.jpg';
import TractorGuideStartStraight from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_start-straight.jpg';
import TractorGuideUseUturn from '../../../../assets/images/pluvaAutoCustomerService/tractor-guide_use-uturn.jpg';
import PLUVA_AUTO_LOGO from '../../../../assets/svg/pluva-auto-logo.svg';
import { Button } from '../../../../components/common/buttons/Button';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { updateManifest } from '../../../../utils/updater/updateManifest';
import { VideoCardRow } from './components/VideoCardRow';

export interface IPluvaAutoCSVideo {
  thumbnailUrl: string;
  videoTitle: string;
  youtubeUrl: string;
}

const pluvaAutoCSTractorVideos: IPluvaAutoCSVideo[] = [
  {
    thumbnailUrl: TractorGuideGPSConnected,
    videoTitle: '전원 켜기 및 GPS 연결 방법',
    youtubeUrl: 'https://www.youtube.com/watch?v=llWjG2pUF5Q',
  },
  {
    thumbnailUrl: TractorGuideDualTractorUsage,
    videoTitle: '2대의 트랙터에 플루바 오토 사용하는 방법',
    youtubeUrl: 'https://www.youtube.com/watch?v=4ZU5AJ1gzZQ',
  },
  {
    thumbnailUrl: TractorGuideStartStraight,
    videoTitle: '직진 자율주행 시작하기',
    youtubeUrl: 'https://www.youtube.com/watch?v=29PtuFUMwyM',
  },
  {
    thumbnailUrl: TractorGuideFeatureSet,
    videoTitle: '자율주행 작업 시 사용가능한 다양한 기능',
    youtubeUrl: 'https://www.youtube.com/watch?v=Otm-ZAx8Zyw',
  },
  {
    thumbnailUrl: TractorGuidePrepareUturn,
    videoTitle: '유턴 기능 준비하기',
    youtubeUrl: 'https://www.youtube.com/watch?v=VNsRxOkZasg',
  },
  {
    thumbnailUrl: TractorGuideUseUturn,
    videoTitle: '유턴 기능 사용하기',
    youtubeUrl: 'https://www.youtube.com/watch?v=MNd7L5FKm3E',
  },
  {
    thumbnailUrl: TractorGuideAttachmentMachine,
    videoTitle: '새로운 부착기 등록 방법',
    youtubeUrl: 'https://www.youtube.com/watch?v=i1jMHPFJShs',
  },
];

const pluvaAutoManualVideos: IPluvaAutoCSVideo[] = [
  {
    thumbnailUrl: ManualWirelessUpdate,
    videoTitle: '신기능 무선 업데이트 방법',
    youtubeUrl: 'https://www.youtube.com/watch?v=ZXpekHgqJts',
  },
  {
    thumbnailUrl: ManualStartTroubleshoot,
    videoTitle: '자율주행이 시작하지 않는다면?',
    youtubeUrl: 'https://www.youtube.com/watch?v=Pa97nQQA2AA',
  },
  {
    thumbnailUrl: ManualShutdownTroubleshoot,
    videoTitle: '자율주행이 저절로 종료 된다면?',
    youtubeUrl: 'https://www.youtube.com/watch?v=PP17V6Bypmo',
  },
  {
    thumbnailUrl: ManualStraightLineAssist,
    videoTitle: '직진 작업이 더 반듯할 수 있나요?',
    youtubeUrl: 'https://www.youtube.com/watch?v=Ebk3cllcdvQ',
  },
];

export const PluvaAutoCustomerServicePage = () => {
  const navigate = useNavigate();
  const tractorGuideSectionRef = useRef<HTMLHeadingElement>(null);
  const manualSectionRef = useRef<HTMLHeadingElement>(null);

  const scrollToRef = (ref: RefObject<HTMLHeadingElement>) => {
    if (ref) {
      ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  };

  const handlePluvaAutoInquiryButton = () => {
    ampli.오토플루바오토문의하기버튼클릭({
      page_type: 'cs',
    });
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:15335502' });
    } else {
      window.location.href = 'tel:15335502';
    }
  };

  useEffect(() => {
    updateManifest({ manifest: { name: pageInfo.pluvaAutoCustomerServicePage.title } });
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageInfo.pluvaAutoCustomerServicePage.title}</title>
      </Helmet>

      <FixedHeaderSection>
        <FixedHeaderLogoWrapper>
          <img src={PLUVA_AUTO_LOGO} alt={'pluva-auto-logo'} />
          <Button
            borderType={'square'}
            size={'sm'}
            color={'filledGreyStrong'}
            text={{ content: '사용 후기 보기', size: 'md' }}
            icon={{ position: 'right', size: 'Medium', fileName: 'AngleRight', fill: '#fff' }}
            onClick={() => {
              ampli.pvmClickGoToAutoSalesPage();
              ReactGA.event('pvm_click_go_to_auto_sales_page');
              navigate(pageInfo.pluvaAutoBusinessPage.path);
            }}
          />
        </FixedHeaderLogoWrapper>

        <FixedHeaderNavBarWrapper>
          <FixedHeaderNavBar>
            <NavItem onClick={() => scrollToRef(tractorGuideSectionRef)}>{'트랙터 사용법'}</NavItem>
            <NavItem onClick={() => scrollToRef(manualSectionRef)}>{'영상 설명서'}</NavItem>
          </FixedHeaderNavBar>
        </FixedHeaderNavBarWrapper>
      </FixedHeaderSection>

      <Container>
        <TitleSection>
          <TitleWrapper>
            <Title>{'플루바 오토의 사용법이 궁금하신가요?'}</Title>
          </TitleWrapper>
          <SubTitle>{'트랙터 자율 주행 사용법부터 플루바 오토 업데이트 방법까지 영상으로 안내 해드려요!'}</SubTitle>
        </TitleSection>

        <ContentSection>
          <ContentTitle ref={tractorGuideSectionRef}>{'플루바 오토 사용법 - 트랙터 편'}</ContentTitle>
          <CardContainer>
            {pluvaAutoCSTractorVideos.map((video) => {
              return <VideoCardRow video={video} />;
            })}
          </CardContainer>
        </ContentSection>

        <ContentSection>
          <ContentTitle ref={manualSectionRef}>{'영상 설명서'}</ContentTitle>
          <CardContainer>
            {pluvaAutoManualVideos.map((video) => {
              return <VideoCardRow video={video} />;
            })}
          </CardContainer>
        </ContentSection>
      </Container>

      <BlurSection />

      <FixedBottomSection>
        <CSButton onClick={handlePluvaAutoInquiryButton}>
          <CSButtonText>{'CS 문의하기'}</CSButtonText>
        </CSButton>
      </FixedBottomSection>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  background-color: white;

  padding-top: 110px;
  padding-bottom: 140px;
`;

const FixedHeaderSection = styled.div`
  position: fixed;

  top: 0px;
  left: 0px;

  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background: var(--Light-Scheme-Monochrome-white, #fff);
  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  z-index: 55;

  @media (max-width: 768px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const FixedHeaderLogoWrapper = styled.div`
  display: flex;
  padding: 24px;
  justify-content: space-between;
  width: 100%;

  @media (min-width: 768px) {
    padding: 24px 120px;
    width: auto;
    min-width: 768px;
  }
`;

const FixedHeaderNavBarWrapper = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) 24px;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  @media (max-width: 768px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const FixedHeaderNavBar = styled.ul`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;

  @media (min-width: 768px) {
    min-width: 768px;
    padding: 0px 120px;
  }
`;

const NavItem = styled.li`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */

  cursor: pointer;
`;

const FixedBottomSection = styled.div`
  position: fixed;

  width: 100%;
  max-width: 768px;

  bottom: 0px;
  left: 50%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: 0px var(--radius-large, 16px) 32px var(--radius-large, 16px);
  gap: var(--radius-medium, 8px);

  transform: translate(-50%);
  background-color: white;

  z-index: 100;
`;

const CSButton = styled.button`
  height: 60px;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);

  @media screen and (max-width: 768px) {
    height: 40px;
  }
`;

const CSButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 17px */

  @media screen and (max-width: 768px) {
    color: var(--Light-Scheme-Monochrome-white, #fff);

    /* System/Large/l-medium */
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 85%; /* 13.6px */
  }
`;

const BlurSection = styled.div`
  position: fixed;

  left: 50%;
  bottom: 92px;

  transform: translateX(-50%);

  width: 100%;
  max-width: 768px;
  height: 40px;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  @media screen and (max-width: 768px) {
    bottom: 72px;
  }
`;

const TitleSection = styled.div`
  width: 100%;
  max-width: 768px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: var(--radius-large, 16px);
  padding: 60px 120px;

  @media screen and (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const TitleWrapper = styled.div`
  height: 38px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-family: Pretendard;
  font-size: 32px;
  font-style: normal;
  font-weight: 800;
  line-height: 120%;

  @media screen and (max-width: 768px) {
    font-size: 24px;
    font-weight: 700;
    line-height: 130%;
  }
`;

const SubTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/XX-Large/xxl-medium */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 30px */

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 400;
  }
`;

const ContentSection = styled.div`
  display: flex;
  width: 100%;
  max-width: 768px;
  padding: 0px 120px 60px 120px;
  flex-direction: column;
  flex: 1 0 0;
  gap: var(--radius-large, 16px);

  @media (max-width: 768px) {
    padding: 0px 24px 60px 24px;
  }
`;

const ContentTitle = styled.h3`
  scroll-margin: 134px;
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;
